/**
 * Unify Location model.
 *
 * Represents a physical practice location for intake scheduling and
 * provider directory display. Locations are linked to Schedules which
 * contain available Slots for booking.
 *
 * Practice **sites** use `physicalType` = building (`bu`); nested **rooms**
 * use `physicalType` = room (`ro`) and `partOf` → site. See
 * docs/prd/LocationsRoomsEquipment/PRD_LOCATIONS_ROOMS_EQUIPMENT_FHIR_MAPPING.md.
 */
import type { Extension, Location, Organization, Reference } from '@medplum/fhirtypes';

import {
  ADA_ACCESSIBLE_EXT,
  EXTERNAL_BOOKABLE_EXT,
  LOCATION_ARRIVAL_INSTRUCTIONS_EXT,
  LOCATION_DISPLAY_ADDRESS_OVERRIDE_EXT,
  LOCATION_IDENTIFIER_SYSTEM,
  LOCATION_PARKING_INSTRUCTIONS_EXT,
  LOCATION_PHYSICAL_TYPE_SYSTEM,
  LOCATION_WAITING_ROOM_INSTRUCTIONS_EXT,
  ROOM_CAPACITY_EXT,
  ROOM_TYPE_SYSTEM,
} from '../constants';
import { UNIFY_PROFILES } from '../profiles';
import {
  DAY_OF_WEEK_CODES,
  DEFAULT_PROVIDER_WEEKLY_AVAILABILITY_TIMEZONE,
  type DayOfWeekCode,
} from './schedule';

// ═══════════════════════════════════════════════════════════════════════
//  Literal union types
// ═══════════════════════════════════════════════════════════════════════

/** Location statuses used by Unify. */
export type LocationStatus = 'active' | 'inactive';

/** Location mode. */
export type LocationMode = 'instance' | 'kind';

export type PracticeSiteRoomTypeCode = 'therapy-room' | 'phone-booth';

/** One open window on a weekday (office wall clock, minutes from midnight). */
export interface LocationDayHours {
  openingMinutes: number;
  closingMinutes: number;
}

/** Weekly office hours keyed by FHIR `daysOfWeek` codes. Missing days are closed. */
export type LocationWeeklyHours = Partial<Record<DayOfWeekCode, LocationDayHours>>;

const WEEKDAY_INDEX_TO_DAY: DayOfWeekCode[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/** Mon–Fri 9:00–18:00 local — new sites and local/demo seed. */
export function getDefaultSiteWeeklyHours(): LocationWeeklyHours {
  const hours: LocationWeeklyHours = {};
  for (const day of ['mon', 'tue', 'wed', 'thu', 'fri'] as const) {
    hours[day] = { openingMinutes: 9 * 60, closingMinutes: 18 * 60 };
  }
  return hours;
}

export function fhirTimeToMinutes(value: string | undefined): number | undefined {
  if (!value) return undefined;
  const match = value.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return undefined;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || hour > 23 || minute > 59) return undefined;
  return hour * 60 + minute;
}

export function minutesToFhirTime(totalMinutes: number): string {
  const clamped = Math.max(0, Math.min(24 * 60, Math.round(totalMinutes)));
  const hour = Math.min(23, Math.floor(clamped / 60));
  const minute = clamped % 60;
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:00`;
}

function isValidDayWindow(window: LocationDayHours | undefined): window is LocationDayHours {
  return (
    window != null
    && Number.isFinite(window.openingMinutes)
    && Number.isFinite(window.closingMinutes)
    && window.openingMinutes >= 0
    && window.closingMinutes <= 24 * 60
    && window.openingMinutes < window.closingMinutes
  );
}

export function getLocationWeeklyHours(resource: Location | undefined): LocationWeeklyHours {
  const hours: LocationWeeklyHours = {};
  for (const entry of resource?.hoursOfOperation ?? []) {
    const openingMinutes = fhirTimeToMinutes(entry.openingTime);
    const closingMinutes = fhirTimeToMinutes(entry.closingTime);
    if (openingMinutes == null || closingMinutes == null || openingMinutes >= closingMinutes) continue;
    for (const raw of entry.daysOfWeek ?? []) {
      const day = DAY_OF_WEEK_CODES.find((code) => code === raw);
      if (!day) continue;
      hours[day] = { openingMinutes, closingMinutes };
    }
  }
  return hours;
}

export function locationHasWeeklyHours(hours: LocationWeeklyHours | undefined): boolean {
  return DAY_OF_WEEK_CODES.some((day) => isValidDayWindow(hours?.[day]));
}

export function roomHasCustomHours(room: Location | undefined): boolean {
  return locationHasWeeklyHours(getLocationWeeklyHours(room));
}

export function toHoursOfOperation(hours: LocationWeeklyHours | undefined): NonNullable<Location['hoursOfOperation']> | undefined {
  const groups = new Map<string, DayOfWeekCode[]>();
  for (const day of DAY_OF_WEEK_CODES) {
    const window = hours?.[day];
    if (!isValidDayWindow(window)) continue;
    const key = `${window.openingMinutes}-${window.closingMinutes}`;
    const list = groups.get(key) ?? [];
    list.push(day);
    groups.set(key, list);
  }
  if (groups.size === 0) return undefined;
  return [...groups.entries()].map(([key, daysOfWeek]) => {
    const [openingMinutes, closingMinutes] = key.split('-').map(Number);
    return {
      daysOfWeek,
      openingTime: minutesToFhirTime(openingMinutes),
      closingTime: minutesToFhirTime(closingMinutes),
    };
  });
}

export function setLocationHoursOfOperation(
  resource: Location,
  hours: LocationWeeklyHours | undefined,
): Location {
  const hoursOfOperation = toHoursOfOperation(hours);
  if (!hoursOfOperation) {
    const rest = { ...resource };
    delete rest.hoursOfOperation;
    return rest;
  }
  return { ...resource, hoursOfOperation };
}

export function clampHoursToParent(
  roomHours: LocationWeeklyHours,
  siteHours: LocationWeeklyHours,
): LocationWeeklyHours {
  const next: LocationWeeklyHours = {};
  for (const day of DAY_OF_WEEK_CODES) {
    const room = roomHours[day];
    const site = siteHours[day];
    if (!isValidDayWindow(room) || !isValidDayWindow(site)) continue;
    const openingMinutes = Math.max(room.openingMinutes, site.openingMinutes);
    const closingMinutes = Math.min(room.closingMinutes, site.closingMinutes);
    if (openingMinutes < closingMinutes) {
      next[day] = { openingMinutes, closingMinutes };
    }
  }
  return next;
}

export function applyClampedCustomHoursToRoom(
  room: Location,
  siteHours: LocationWeeklyHours,
): Location {
  if (!roomHasCustomHours(room)) return room;
  const clamped = clampHoursToParent(getLocationWeeklyHours(room), siteHours);
  if (!locationHasWeeklyHours(clamped)) {
    return setLocationHoursOfOperation({ ...room, status: 'inactive' }, undefined);
  }
  return setLocationHoursOfOperation(room, clamped);
}

export function customHoursFitParent(
  roomHours: LocationWeeklyHours,
  siteHours: LocationWeeklyHours,
): boolean {
  if (!locationHasWeeklyHours(roomHours)) return false;
  for (const day of DAY_OF_WEEK_CODES) {
    const room = roomHours[day];
    if (!isValidDayWindow(room)) continue;
    const site = siteHours[day];
    if (!isValidDayWindow(site)) return false;
    if (room.openingMinutes < site.openingMinutes || room.closingMinutes > site.closingMinutes) {
      return false;
    }
  }
  return true;
}

export function resolveEffectiveWeeklyHours(
  siteHours: LocationWeeklyHours | undefined,
  roomHours?: LocationWeeklyHours,
): LocationWeeklyHours | undefined {
  if (!locationHasWeeklyHours(siteHours)) return undefined;
  if (locationHasWeeklyHours(roomHours)) return roomHours;
  return siteHours;
}

/** Effective bookable hours: inherit site, or stored custom room hours. Undefined = not bookable. */
export function getEffectiveRoomHours(
  site: Location | undefined,
  room: Location | undefined,
): LocationWeeklyHours | undefined {
  return resolveEffectiveWeeklyHours(getLocationWeeklyHours(site), getLocationWeeklyHours(room));
}

export function getZonedWeekdayAndMinutes(
  iso: string,
  timeZone: string = DEFAULT_PROVIDER_WEEKLY_AVAILABILITY_TIMEZONE,
): { day: DayOfWeekCode; minutes: number } | undefined {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return undefined;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(date);
  const weekday = parts.find((p) => p.type === 'weekday')?.value;
  const hour = Number(parts.find((p) => p.type === 'hour')?.value);
  const minute = Number(parts.find((p) => p.type === 'minute')?.value);
  const day = WEEKDAY_INDEX_TO_DAY.find((code) => {
    const label = { sun: 'Sun', mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat' }[code];
    return label === weekday;
  });
  if (!day || !Number.isFinite(hour) || !Number.isFinite(minute)) return undefined;
  return { day, minutes: hour * 60 + minute };
}

export function isIntervalWithinLocationHours(
  startIso: string,
  endIso: string,
  hours: LocationWeeklyHours | undefined,
  timeZone: string = DEFAULT_PROVIDER_WEEKLY_AVAILABILITY_TIMEZONE,
): boolean {
  if (!locationHasWeeklyHours(hours)) return false;
  if (!hours) return false;
  const start = getZonedWeekdayAndMinutes(startIso, timeZone);
  const end = getZonedWeekdayAndMinutes(endIso, timeZone);
  if (!start || !end) return false;
  let endMinutes = end.minutes;
  if (start.day !== end.day) {
    if (end.minutes !== 0) return false;
    endMinutes = 24 * 60;
  }
  if (start.minutes >= endMinutes) return false;
  const window = hours[start.day];
  if (!isValidDayWindow(window)) return false;
  return start.minutes >= window.openingMinutes && endMinutes <= window.closingMinutes;
}

// ═══════════════════════════════════════════════════════════════════════
//  Private helpers
// ═══════════════════════════════════════════════════════════════════════

function getExt(resource: Location, url: string): Extension | undefined {
  return resource.extension?.find((e) => e.url === url);
}

function setExt(resource: Location, url: string, ext: Partial<Extension>): Location {
  const extensions = (resource.extension ?? []).filter((e) => e.url !== url);
  extensions.push({ url, ...ext } as Extension);
  return { ...resource, extension: extensions };
}

/** Physical type code from HL7 location-physical-type (e.g. `bu`, `ro`). */
export function getLocationPhysicalTypeCode(resource: Location): string | undefined {
  return resource.physicalType?.coding?.find((c) => c.system === LOCATION_PHYSICAL_TYPE_SYSTEM)?.code;
}

export function isPracticeSiteLocation(resource: Location): boolean {
  return getLocationPhysicalTypeCode(resource) === 'bu';
}

export function isRoomLocation(resource: Location): boolean {
  return getLocationPhysicalTypeCode(resource) === 'ro';
}

export function getRoomTypeCode(resource: Location): PracticeSiteRoomTypeCode | undefined {
  const code = resource.type?.[0]?.coding?.find((c) => c.system === ROOM_TYPE_SYSTEM)?.code;
  if (code === 'therapy-room' || code === 'phone-booth') return code;
  return undefined;
}

export function getRoomCapacity(resource: Location): number | undefined {
  return getExt(resource, ROOM_CAPACITY_EXT)?.valuePositiveInt;
}

export function setRoomCapacity(resource: Location, capacity: number): Location {
  return setExt(resource, ROOM_CAPACITY_EXT, { valuePositiveInt: capacity });
}

export function getAdaAccessible(resource: Location): boolean | undefined {
  return getExt(resource, ADA_ACCESSIBLE_EXT)?.valueBoolean;
}

export function setAdaAccessible(resource: Location, value: boolean): Location {
  return setExt(resource, ADA_ACCESSIBLE_EXT, { valueBoolean: value });
}

export function getExternalBookable(resource: Location | undefined): boolean | undefined {
  if (!resource) return undefined;
  return getExt(resource, EXTERNAL_BOOKABLE_EXT)?.valueBoolean;
}

export function setExternalBookable(resource: Location, value: boolean): Location {
  return setExt(resource, EXTERNAL_BOOKABLE_EXT, { valueBoolean: value });
}

/** Client-facing parking instructions for in-person visits (e.g. rendered in appointment emails). */
export function getLocationParkingInstructions(resource: Location | undefined): string | undefined {
  return resource ? getExt(resource, LOCATION_PARKING_INSTRUCTIONS_EXT)?.valueString : undefined;
}

export function setLocationParkingInstructions(resource: Location, value: string): Location {
  return setExt(resource, LOCATION_PARKING_INSTRUCTIONS_EXT, { valueString: value });
}

/** Client-facing arrival instructions for in-person visits. */
export function getLocationArrivalInstructions(resource: Location | undefined): string | undefined {
  return resource ? getExt(resource, LOCATION_ARRIVAL_INSTRUCTIONS_EXT)?.valueString : undefined;
}

export function setLocationArrivalInstructions(resource: Location, value: string): Location {
  return setExt(resource, LOCATION_ARRIVAL_INSTRUCTIONS_EXT, { valueString: value });
}

/** Client-facing waiting room instructions for in-person visits. */
export function getLocationWaitingRoomInstructions(resource: Location | undefined): string | undefined {
  return resource ? getExt(resource, LOCATION_WAITING_ROOM_INSTRUCTIONS_EXT)?.valueString : undefined;
}

export function setLocationWaitingRoomInstructions(resource: Location, value: string): Location {
  return setExt(resource, LOCATION_WAITING_ROOM_INSTRUCTIONS_EXT, { valueString: value });
}

/** Optional override of the display address rendered in client-facing communications. */
export function getLocationDisplayAddressOverride(resource: Location | undefined): string | undefined {
  return resource ? getExt(resource, LOCATION_DISPLAY_ADDRESS_OVERRIDE_EXT)?.valueString : undefined;
}

export function setLocationDisplayAddressOverride(resource: Location, value: string): Location {
  return setExt(resource, LOCATION_DISPLAY_ADDRESS_OVERRIDE_EXT, { valueString: value });
}

function physicalTypeBuilding(): NonNullable<Location['physicalType']> {
  return {
    coding: [
      {
        system: LOCATION_PHYSICAL_TYPE_SYSTEM,
        code: 'bu',
        display: 'Building',
      },
    ],
  };
}

function physicalTypeRoom(): NonNullable<Location['physicalType']> {
  return {
    coding: [
      {
        system: LOCATION_PHYSICAL_TYPE_SYSTEM,
        code: 'ro',
        display: 'Room',
      },
    ],
  };
}

/** Reference id only, e.g. `Location/abc` → `abc`. */
export function parseLocationRefId(ref: Reference | undefined): string | undefined {
  const r = ref?.reference;
  if (!r?.startsWith('Location/')) return undefined;
  return r.slice('Location/'.length);
}

// ═══════════════════════════════════════════════════════════════════════
//  Builders (PRD-aligned)
// ═══════════════════════════════════════════════════════════════════════

export interface PracticeSiteLocationInput {
  name: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zipCode: string;
  phone?: string;
  latitude?: number;
  longitude?: number;
  status: LocationStatus;
  hours: LocationWeeklyHours;
}

/**
 * Build a FHIR Location for a practice **site** (building).
 * Caller should `createResource` or merge with existing `id` for updates.
 */
export function buildPracticeSiteLocation(
  organizationId: string,
  organizationDisplay: string | undefined,
  input: PracticeSiteLocationInput,
  existing?: Location,
): Location {
  const orgRef: Reference<Organization> = {
    reference: `Organization/${organizationId}`,
    display: organizationDisplay,
  };
  const line = [input.addressLine1, input.addressLine2].filter(
    (s): s is string => Boolean(s && String(s).trim()),
  );
  const position =
    input.latitude != null && input.longitude != null
      ? { latitude: input.latitude, longitude: input.longitude }
      : undefined;

  const externalBookable = getExternalBookable(existing) ?? false;
  const otherExt = (existing?.extension ?? []).filter((e) => e.url !== EXTERNAL_BOOKABLE_EXT);
  const extensions: Extension[] = [...otherExt, { url: EXTERNAL_BOOKABLE_EXT, valueBoolean: externalBookable }];

  const identifier =
    existing?.id != null
      ? [
          ...(existing.identifier?.filter((i) => i.system !== LOCATION_IDENTIFIER_SYSTEM) ?? []),
          { system: LOCATION_IDENTIFIER_SYSTEM, value: existing.id },
        ]
      : existing?.identifier;

  return {
    resourceType: 'Location',
    ...(existing?.id ? { id: existing.id } : {}),
    meta: {
      ...existing?.meta,
      profile: [UNIFY_PROFILES.locationSite],
    },
    ...(identifier && identifier.length > 0 ? { identifier } : {}),
    status: input.status,
    name: input.name,
    mode: 'instance',
    type: [
      {
        coding: [
          {
            system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
            code: 'OF',
            display: 'Outpatient Facility',
          },
        ],
      },
    ],
    physicalType: physicalTypeBuilding(),
    telecom: input.phone
      ? [{ system: 'phone', value: input.phone, use: 'work' }]
      : undefined,
    address: {
      use: 'work',
      type: 'physical',
      line,
      city: input.city,
      state: input.state,
      postalCode: input.zipCode,
      country: 'US',
    },
    position,
    managingOrganization: orgRef,
    extension: extensions.length > 0 ? extensions : undefined,
    hoursOfOperation: toHoursOfOperation(input.hours),
  };
}

export interface PracticeRoomLocationInput {
  name: string;
  roomType: PracticeSiteRoomTypeCode;
  capacity: number;
  adaAccessible: boolean;
  status: LocationStatus;
  /** Custom hours; omit or leave empty to inherit the parent site. */
  hours?: LocationWeeklyHours;
}

/**
 * Build a FHIR Location for a **room** under a practice site.
 */
export function buildPracticeRoomLocation(
  organizationId: string,
  organizationDisplay: string | undefined,
  siteLocationId: string,
  siteDisplay: string | undefined,
  input: PracticeRoomLocationInput,
  existing?: Location,
): Location {
  const orgRef: Reference<Organization> = {
    reference: `Organization/${organizationId}`,
    display: organizationDisplay,
  };
  const cap = input.roomType === 'phone-booth' ? 1 : Math.max(1, input.capacity);
  const identifier =
    existing?.id != null
      ? [
          ...(existing.identifier?.filter((i) => i.system !== LOCATION_IDENTIFIER_SYSTEM) ?? []),
          { system: LOCATION_IDENTIFIER_SYSTEM, value: existing.id },
        ]
      : existing?.identifier;

  let loc: Location = {
    resourceType: 'Location',
    ...(existing?.id ? { id: existing.id } : {}),
    meta: {
      ...existing?.meta,
      profile: [UNIFY_PROFILES.locationRoom],
    },
    ...(identifier && identifier.length > 0 ? { identifier } : {}),
    status: input.status,
    name: input.name,
    mode: 'instance',
    type: [
      {
        coding: [
          {
            system: ROOM_TYPE_SYSTEM,
            code: input.roomType,
            display: input.roomType === 'phone-booth' ? 'Phone Booth' : 'Therapy Room',
          },
        ],
      },
    ],
    physicalType: physicalTypeRoom(),
    partOf: { reference: `Location/${siteLocationId}`, display: siteDisplay },
    managingOrganization: orgRef,
    extension: [],
  };
  loc = setRoomCapacity(loc, cap);
  loc = setAdaAccessible(loc, input.adaAccessible);
  loc = setLocationHoursOfOperation(loc, input.hours);
  return loc;
}

// ═══════════════════════════════════════════════════════════════════════
//  Getter helpers
// ═══════════════════════════════════════════════════════════════════════

/** Returns the Location name. */
export function getLocationName(resource: Location): string | undefined {
  return resource.name;
}

/** Returns the managing organization reference. */
export function getLocationOrganizationRef(resource: Location): Reference | undefined {
  return resource.managingOrganization;
}

/** Returns the full address as a formatted single-line string. */
export function getLocationAddressLine(resource: Location): string | undefined {
  const addr = resource.address;
  if (!addr) return undefined;
  const parts = [
    addr.line?.join(', '),
    addr.city,
    addr.state,
    addr.postalCode,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : undefined;
}

/** Display address for client-facing communications: `location-display-address-override` extension if set, else the formatted address line. */
export function getLocationDisplayAddress(resource: Location): string | undefined {
  return getLocationDisplayAddressOverride(resource) ?? getLocationAddressLine(resource);
}

/** Returns the state code from the address. */
export function getLocationState(resource: Location): string | undefined {
  return resource.address?.state;
}

/** Returns the city from the address. */
export function getLocationCity(resource: Location): string | undefined {
  return resource.address?.city;
}

/** Returns the postal code from the address. */
export function getLocationPostalCode(resource: Location): string | undefined {
  return resource.address?.postalCode;
}

/** Work phone from telecom. */
export function getLocationWorkPhone(resource: Location): string | undefined {
  return resource.telecom?.find((t) => t.system === 'phone' && (t.use === 'work' || !t.use))?.value;
}
